import React, { useMemo } from "react";
import moment from "moment-timezone";

import { cn } from "@/lib/utils";
import Select from "./Select";

// `value` is the IANA zone name itself (e.g. "Asia/Kolkata") — the only
// unambiguous identifier for a timezone, and what the backend validates
// against (Python's zoneinfo.available_timezones()). Abbreviations like
// "IST"/"CST" collide across regions and don't survive DST transitions, so
// they're shown only as a hint in the label, never stored as the value.
//
// Built once per session: `moment.tz.names()` is ~600 zones and the
// abbreviation for each needs a `moment.tz()` call, which is far too much to
// redo on every render of a form.
const buildTimezones = () =>
  moment.tz.names().map((tz) => {
    const abbr = moment.tz(Date.now(), tz).zoneAbbr();
    const hasAbbr = abbr && !/^[+-]?\d/.test(abbr);
    // The label carries the full zone name, so searching it finds the region
    // ("Asia"), the city ("Kolkata") and the abbreviation ("IST") alike.
    return { value: tz, label: hasAbbr ? `${tz} (${abbr})` : tz };
  });

/**
 * Timezone picker — the app's `Select`, with the zone list.
 *
 * The list is portalled and placed by `Select`: it escapes the modal or card it
 * sits in, flips above the trigger when there is no room below, and is capped
 * to the space actually available. Its own absolutely-positioned dropdown used
 * to open *inside* the shift modal's scrolling body, so on the org-settings
 * screen the options were clipped and the reader had to scroll the modal to
 * reach them.
 *
 * `error` is the message to show, not a boolean. The field paints its own red
 * border AND prints the sentence below itself, the way every RHF* field does —
 * callers used to pass the message here and print it again in a sibling <p>, so
 * the user read the same sentence twice.
 */
const TimezoneSelect = ({ value, onChange, label, className = "", error, disabled = false }) => {
  const timezones = useMemo(buildTimezones, []);

  return (
    <div className={cn("w-full", className)}>
      {label && (
        <label className="block text-xs mb-1 text-text-muted" htmlFor="timezone-select">
          {label}
        </label>
      )}

      <Select
        id="timezone-select"
        value={value || ""}
        onChange={(next) => onChange?.(next)}
        options={timezones}
        placeholder="Select timezone"
        searchable
        searchPlaceholder="Search timezone..."
        disabled={disabled}
        hasError={!!error}
        // Matches the height and fill of the form fields beside it, the same
        // way RHFSelect restores them.
        className="h-11 px-4 py-2.5 font-normal bg-background-form/40"
      />

      {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default TimezoneSelect;
